import { api } from '@/apis';
import { log } from '@/utils';
import { useQuery, useQueryClient } from '@tanstack/react-query';

const groupTypes = new Map([
  ['vehicle', 'vehicles'],
  ['location', 'locations'],
  ['event', 'events'],
  ['incident', 'incidents'],
  ['person', 'people'],
  ['objective', 'objectives'],
]);

function getInitialFeature(initialState) {
  const { stateKey, point, position, boundary, stateType, ...properties } =
    initialState;
  return {
    type: 'Feature',
    id: stateKey,
    geometry: point || position || boundary,
    properties: { ...properties, type: stateType },
  };
}

function getPaths(initialStates, stateChanges, type) {
  return initialStates
    .filter((state) => type === state.stateType)
    .map((state) => {
      const initialFeature = getInitialFeature(state);
      return {
        id: initialFeature.id,
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: [
            ...(initialFeature.geometry
              ? [initialFeature.geometry.coordinates]
              : []),
            ...stateChanges
              .filter(
                (change) =>
                  change.stateKey === initialFeature.id && change.position,
              )
              .map((change) => change.position.coordinates),
          ],
        },
      };
    });
}

function getProperties(groupType, time, properties) {
  switch (groupType) {
    case 'vehicles': {
      const {
        fleetNumber,
        homeStation,
        identificationNumber,
        registrationNumber,
        role,
        telematicsBoxImei: imei,
        type,
        groups,
        areas,
        locations,
        startTime: time,
        endTime: _,
        ...rest
      } = properties;
      const status = properties.emergencyEquipmentOn
        ? 'emergencyEquipmentOn'
        : properties.malfunctionIndicatorLightOn
          ? 'malfunctionIndicatorLightOn'
          : properties.ignitionOn
            ? 'ignitionOn'
            : 'ignitionOff';

      return {
        ...rest,
        status,
        imei,
        time,
        vehicle: {
          fleetNumber,
          homeStation,
          identificationNumber,
          registrationNumber,
          role,
          type,
          groups,
          areas,
          locations,
        },
      };
    }
    case 'people': {
      const {
        areas,
        category,
        code,
        collarNumber,
        forenames,
        radioSsi: ssi,
        role,
        surname,
        wards,
        groups,
        startTime: time,
        endTime: _,
        ...rest
      } = properties;
      // const status =

      return {
        ...rest,
        // status,
        ssi,
        time,
        person: {
          areas,
          category,
          code,
          collarNumber,
          forenames,
          role,
          surname,
          wards,
          groups,
        },
      };
    }
    case 'incidents': {
      const status =
        time >= properties.closedTime
          ? 'closed'
          : time >= properties.attendedTime
            ? 'attended'
            : time >= properties.assignedTime
              ? 'assigned'
              : 'opened';
      return {
        ...properties,
        status,
      };
    }
    default:
      return properties;
  }
}

export function useEventReplay(type, id) {
  const queryKey = ['eventReplay', type, id];
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey,
    queryFn: async ({ signal }) => {
      const { initialStates, stateChanges } = await api
        .get(`replay/${type}/${id}`, { signal })
        .json();

      // a location's start/end shouldn't determine the replay start/end so let's
      // get the min/max of moving object times and clamp the non-moving to it
      const movingObjects = ['vehicle', 'person'];
      let { startTime, endTime } = stateChanges[0];
      stateChanges
        .filter((change) => movingObjects.includes(change.stateType))
        .forEach((change) => {
          startTime =
            startTime > change.startTime ? change.startTime : startTime;
          endTime = endTime < change.endTime ? change.endTime : endTime;
        });

      // update any locations to start when the earliest moving object starts
      stateChanges
        .filter((change) => !movingObjects.includes(change.stateType))
        .forEach((change) => {
          change.startTime =
            startTime > change.startTime ? startTime : change.startTime;
          change.endTime = endTime < change.endTime ? endTime : change.endTime;
        });

      const replay = [
        ...new Set(
          stateChanges
            .map((stateChange) =>
              startTime > stateChange.startTime
                ? startTime
                : stateChange.startTime,
            )
            .sort((a, b) => a - b),
        ),
      ].reduce(
        (replay, time) => {
          const frames = replay.frames
            .concat(
              stateChanges
                .filter(
                  (stateChange) =>
                    stateChange.startTime <= time && time < stateChange.endTime,
                )
                .reduce(
                  (frame, stateChange) => {
                    const {
                      stateKey,
                      point,
                      position,
                      boundary,
                      ...properties
                    } = stateChange;
                    const groupType = groupTypes.get(stateChange.stateType);

                    if (!(groupType in frame.featureCollections)) {
                      frame.featureCollections[groupType] = {
                        type: 'FeatureCollection',
                        features: [],
                      };
                    }

                    const index = frame.featureCollections[
                      groupType
                    ].features.findIndex((feature) => feature.id === stateKey);

                    if (index === -1) {
                      const initialState = initialStates.find(
                        (state) => state.stateKey === stateKey,
                      );
                      const initialFeature = getInitialFeature(initialState);
                      frame.featureCollections[groupType].features =
                        frame.featureCollections[groupType].features.concat({
                          ...initialFeature,
                          geometry:
                            point ||
                            position ||
                            boundary ||
                            initialFeature.geometry,
                          properties: getProperties(groupType, time, {
                            ...initialFeature.properties,
                            ...properties,
                          }),
                        });
                    } else {
                      const existingFeature =
                        frame.featureCollections[groupType].features[index];
                      frame.featureCollections[groupType].features =
                        frame.featureCollections[groupType].features
                          .slice(0, index)
                          .concat({
                            ...existingFeature,
                            geometry:
                              point ||
                              position ||
                              boundary ||
                              existingFeature.geometry,
                            properties: getProperties(groupType, time, {
                              ...existingFeature.properties,
                              ...properties,
                            }),
                          })
                          .concat(
                            frame.featureCollections[groupType].features.slice(
                              index + 1,
                            ),
                          );
                    }

                    return frame;
                  },
                  {
                    time,
                    featureCollections: {},
                  },
                ),
            )
            .sort((a, b) => a.time - b.time);

          const vehiclePaths = getPaths(initialStates, stateChanges, 'vehicle');
          const personPaths = getPaths(initialStates, stateChanges, 'person');
          const paths = {};
          if (vehiclePaths.length > 0) {
            paths.vehicles = {
              type: 'FeatureCollection',
              features: vehiclePaths,
            };
          }
          if (personPaths.length > 0) {
            paths.people = {
              type: 'FeatureCollection',
              features: personPaths,
            };
          }

          return {
            ...replay,
            frames,
            paths,
          };
        },
        {
          id,
          frames: [],
          paths: {
            vehicles: {
              type: 'FeatureCollection',
              features: [],
            },
            people: {
              type: 'FeatureCollection',
              features: [],
            },
          },
        },
      );

      log('View', 'Replay', { id, type });

      return replay;
    },
    placeholderData: {
      frames: [],
      paths: {
        vehicles: { type: 'FeatureCollection', features: [] },
        people: { type: 'FeatureCollection', features: [] },
      },
    },
    staleTime: 1000 * 60 * 60,
  });

  return {
    query,
    cancel: () => queryClient.cancelQueries({ queryKey }),
  };
}
